import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, LogOut, Trash2 } from "lucide-react";
import { IMG } from "../lib/site";
import { LOGIN, LOGOUT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail?.msg || detail);
}

const Admin = () => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("All");

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data.map((l) => ({ ...l, id: l.id || l._id })));
    } catch {
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await axios.get(`${API}/auth/me`);
        setAuthed(true);
        fetchLeads();
      } catch {
        setAuthed(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setError("");
    try {
      await axios.post(`${API}/auth/login`, { email, password });
      setAuthed(true);
      fetchLeads();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Invalid credentials");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch {}
    setAuthed(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/leads/${id}/status`, { status });
      toast.success(`Updated to ${status}`);
      fetchLeads();
    } catch { toast.error("Failed to update status."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`);
      toast.success("Lead deleted");
      fetchLeads();
    } catch { toast.error("Failed to delete lead."); }
  };

  const filtered = tab === "All" ? leads : leads.filter((l) => l.status === tab);

  if (checking) {
    return (
      <div className="bg-[#0A0A0A] min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#C69453] animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="bg-[#0A0A0A] text-white min-h-screen flex flex-col">
        <header className="border-b border-zinc-900 bg-black py-4">
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img src={IMG.logo} alt="logo" className="h-10 w-auto object-contain" />
              <span className="font-heading text-lg font-bold tracking-widest">PRYME EPOXY</span>
            </Link>
            <Link to="/" className="text-zinc-400 hover:text-[#C69453] text-sm uppercase tracking-wider">Back to Site</Link>
          </div>
        </header>
        <main className="max-w-md w-full mx-auto px-6 my-16 flex-grow">
          <div className="bg-zinc-950 border border-zinc-800 p-8 relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-[#C69453]" />
            <h2 className="text-3xl font-light mb-2">Lead Portal</h2>
            <p className="text-zinc-400 text-sm mb-6">Sign in to manage estimate requests.</p>
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 text-sm mb-6">{error}</div>}
            <form onSubmit={login} className="space-y-5">
              <input type="email" required placeholder="tyler@prymeepoxy.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={LOGIN.emailInput} className="w-full h-12 px-4 bg-zinc-900 border border-zinc-800 text-white focus:outline-none focus:border-[#C69453]" />
              <input type="password" required placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid={LOGIN.passwordInput} className="w-full h-12 px-4 bg-zinc-900 border border-zinc-800 text-white focus:outline-none focus:border-[#C69453]" />
              <button type="submit" disabled={loggingIn} data-testid={LOGIN.submitButton} className="w-full bg-[#C69453] hover:bg-[#AE7F44] text-black font-extrabold py-4 flex items-center justify-center gap-2 disabled:opacity-50">
                {loggingIn ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Sign In <ArrowRight className="w-5 h-5" /></>}
              </button>
            </form>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-[#0A0A0A] text-white min-h-screen">
      <header className="border-b border-zinc-800 bg-black/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src={IMG.logo} alt="logo" className="h-10 w-auto object-contain" />
            <div>
              <span className="font-heading text-lg font-bold tracking-widest block">PRYME EPOXY</span>
              <span className="text-[10px] text-[#C69453] tracking-[0.2em] uppercase font-semibold">Lead Management</span>
            </div>
          </div>
          <div className="flex items-center space-x-5">
            <Link to="/" className="text-zinc-300 hover:text-white text-sm uppercase tracking-wider">Live Site</Link>
            <button onClick={logout} data-testid={LOGOUT.button} className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-4 py-2 text-sm uppercase tracking-wider">
              <LogOut className="w-4 h-4 text-red-500" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-light">Project Leads</h1>
          <button onClick={fetchLeads} className="bg-zinc-900 border border-zinc-800 px-5 py-2.5 text-sm uppercase tracking-wider">Refresh</button>
        </div>

        <div className="flex flex-wrap border-b border-zinc-800 mb-8 gap-2">
          {["All", "New", "Contacted", "Quoted", "Won", "Lost"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-5 py-3 text-sm uppercase tracking-wider border-b-2 ${tab === t ? "border-[#C69453] text-white" : "border-transparent text-zinc-400 hover:text-white"}`}>
              {t} ({t === "All" ? leads.length : leads.filter((l) => l.status === t).length})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 text-[#C69453] animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 border border-zinc-800 bg-zinc-950/40 text-zinc-400">No leads in this category yet.</div>
        ) : (
          <div className="border border-zinc-800 bg-zinc-950 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-zinc-800 bg-black text-zinc-400 text-xs uppercase tracking-wider">
                  <th className="p-5">Customer</th><th className="p-5">Project</th><th className="p-5">Location</th><th className="p-5">Submitted</th><th className="p-5">Status</th><th className="p-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {filtered.map((l) => (
                  <tr key={l.id} className="hover:bg-zinc-900/40" data-testid={`admin-lead-row-${l.id}`}>
                    <td className="p-5"><div className="font-bold text-white">{l.name}</div><div className="text-zinc-400">{l.phone}</div><div className="text-zinc-500 text-xs">{l.email}</div></td>
                    <td className="p-5"><div className="font-bold text-white">{l.service_type}</div>{l.square_footage ? <div className="text-zinc-400 text-xs">{l.square_footage} sqft</div> : null}{l.preferred_date ? <div className="text-[#C69453] text-xs mt-1">Preferred: {new Date(l.preferred_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div> : null}{l.details ? <div className="text-zinc-500 text-xs italic mt-1 max-w-xs truncate">&ldquo;{l.details}&rdquo;</div> : null}</td>
                    <td className="p-5"><div className="text-white">{l.city}</div>{l.address && <div className="text-zinc-500 text-xs">{l.address}</div>}</td>
                    <td className="p-5 text-zinc-400">{l.created_at ? new Date(l.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                    <td className="p-5">
                      <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} data-testid={`admin-lead-status-select-${l.id}`} className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-white text-xs uppercase">
                        {["New", "Contacted", "Quoted", "Won", "Lost"].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="p-5 text-right">
                      <button onClick={() => remove(l.id)} data-testid={`admin-lead-delete-button-${l.id}`} className="text-zinc-500 hover:text-red-500 p-2"><Trash2 className="w-5 h-5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
